import { Segmented } from './Primitives';
import { IconMonitor, IconMoon, IconSun } from './icons';
import { useTheme, type ThemeChoice } from './theme';

/**
 * Light, Dark or Match system (DESIGN-SYSTEM §12): one control, the shared segmented one — a track, and one mark
 * that slides to the choice — so it reads as a single setting with three positions, not three buttons. Every
 * position is the same width, so nothing moves but the mark.
 *
 * `tone="chrome"` sits on the navy sidebar as three icons; the default sits on a surface (Your account, a phone's
 * menus) with its words. The words are short ("System"), so the three fit a 320 px phone; each button's name is the
 * full one ("Match system"), which contains the word shown, as a name must (WCAG 2.5.3). Changing the theme does
 * not animate the page's colours (`applyTheme`), only the mark.
 */
export function ThemeSwitch({ tone = 'surface', className }: { tone?: 'surface' | 'chrome'; className?: string }) {
    const { choice, setChoice } = useTheme();
    const chrome = tone === 'chrome';
    const options: readonly { value: ThemeChoice; label: React.ReactNode; icon: React.ReactNode; name: string }[] = [
        { value: 'light', name: 'Light', label: chrome ? null : <span aria-hidden="true">Light</span>, icon: <IconSun size={chrome ? 15 : 16} /> },
        { value: 'dark', name: 'Dark', label: chrome ? null : <span aria-hidden="true">Dark</span>, icon: <IconMoon size={chrome ? 15 : 16} /> },
        { value: 'system', name: 'Match system', label: chrome ? null : <span aria-hidden="true">System</span>, icon: <IconMonitor size={chrome ? 15 : 16} /> },
    ];
    return (
        <Segmented
            label="Appearance"
            options={options}
            value={choice}
            onChange={setChoice}
            equal
            tone={tone}
            size={chrome ? 'sm' : 'md'}
            className={className}
        />
    );
}
